import React, { useEffect, useState, useRef } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';

import {
  BorderRadius,
  BoxShadow,
  TransitionGlobal,
  Gutter,
} from '../../../shared/GlobalStyles';

const ModalStyle = (overlayStyles, contentStyles) => ({
  overlay: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    backgroundColor:
      overlayStyles.backgroundColor || 'rgba(216, 216, 216, 0.5)',
    alignItems: 'center',
    position: 'fixed',
    zIndex: '100',
    top: '0',
    ...(overlayStyles.overflowY && { overflowY: overlayStyles.overflowY }),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: contentStyles.backgroundColor || '#ffffff',
    borderRadius: contentStyles.borderRadius || `${BorderRadius}`,
    boxShadow: `${BoxShadow}`,
    ...(contentStyles.minHeight && { minHeight: contentStyles.minHeight }),
    ...(contentStyles.width && { width: contentStyles.width }),
    ...(contentStyles.minWidth && { minWidth: contentStyles.minWidth }),
    ...(contentStyles.maxWidth && { maxWidth: contentStyles.maxWidth }),
    ...(contentStyles.margin && { margin: contentStyles.margin }),
  },
});

const ModalContentWrapper = styled('div')`
  display: flex;
  flex-grow: 1;
  background-color: ${(props) => props.theme.backgroundColorL2};
  ${(props) => props.modal === 'report-post-modal'
    && `
    padding: 30px;
  `}
  form {
    padding-bottom: ${(props) => (props.modal === 'report-post-modal' ? '30px' : '20px')};
    label {
      color: ${(props) => props.theme.textColor};
    }
    input,
    textarea {
      background-color: ${(props) => props.theme.backgroundColorL3};
    }
    ${(props) => props.modal === 'report-post-modal'
      && `
      width: 100%;
      padding-top: 30px;
    `}
  }
  .select__control,
  .select__menu,
  textarea {
    background-color: ${(props) => props.theme.backgroundColorL3};
    color: ${(props) => props.theme.textColor};
    border-color: ${(props) => props.theme.noBorderColorForm};
  }
  .select__single-value {
    color: ${(props) => props.theme.textColor};
  }
  .spinner-container {
    min-height: 300px;
  }
  ${(props) => props.modal === 'report-post-modal'
    && `
    .form-control {
      min-height: 150px;
    }
  `}
  /* TO-DO: GET RID OF IF REPORT POST MODAL? */
  .form-group {
    padding: ${Gutter} calc(${Gutter} * 3);
  }
  .react-pdf__Page__canvas {
    margin: 0 auto;
  }
  /* TO-DO: GET RID OF IF REPORT POST MODAL? (REST OF STYLES HERE) */
  .modal-button {
    position: absolute;
    margin-left: -40px;
    bottom: 0;
  }
  @media only screen and (min-height: 581px) and (max-height: 768px) {
    .modal-button {
      margin-left: ${(props) => (props.modal === 'video-player-modal' ? '-20px' : '-40px')};
    }
    /* TO-DO: TAKEN FROM FILTER GROUPS MODAL WRAPPER */
    .title-container {
      margin-bottom: 0px;
    }
  }
  @media only screen and (max-height: 580px) {
    .modal-button {
      margin-left: -20px;
    }
    .title-container {
      display: none;
    }
  }
  @media only screen and (max-width: 1024px) {
    .note-title-container {
      margin-bottom: 0px !important;
    }
  }
`;

const FeedModalWrapper = ({
  children,
  isOpen,
  onRequestClose,
  overlayStyles,
  contentStyles,
  modalName,
}) => {
  const [modalDimensions, setModalDimensions] = useState({});
  const modalContentRef = useRef(null);

  const modalClose = () => {
    onRequestClose();
  };

  // TO-DO: CURRENTLY ONLY USED FOR REPORT POST MODAL - USEFUL FOR OTHER MODALS?
  useEffect(() => {
    if (modalName === 'report-post-modal') {
      // Brief delay to allow modal content to render before measuring height
      setTimeout(() => {
        setModalDimensions(modalContentRef.current.clientHeight);
      }, 100);
    }
  }, [modalName]);

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Content {
            opacity: 0;
            transform: translateY(50%);
            &:focus {
              outline: -webkit-focus-ring-color auto 0px !important;
            }
            /* TO-DO: DOES NOT APPLY TO CAPTURE MODAL OR VIDEO PLAYER MODAL */
            @media screen and (max-width: 580px) {
              min-width: 350px !important;
            }
          }

          .ReactModal__Overlay {
            background-color: rgba(216, 216, 216, 0.5);
            opacity: 1;
            z-index: 1000 !important;
            justify-content: center;
          }

          .ReactModal__Overlay--after-open {
            opacity: 1;
            transition: ${TransitionGlobal};
          }

          .ReactModal__Overlay--before-close {
            opacity: 0;
          }

          .ReactModal__Content--after-open {
            opacity: 1;
            transform: translateY(0);
            transition: ${TransitionGlobal};
          }

          .ReactModal__Content--before-close {
            opacity: 0;
            transform: translateY(50%);
          }
          @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
            .ReactModal__Content {
              width: 75vw !important;
            }
          }
          /* TO-DO: CURRENTLY ONLY USED FOR REPORT POST MODAL - USEFUL FOR OTHER MODALS? */
          @media screen and (max-height: ${modalDimensions}px) {
            .ReactModal__Content {
              position: absolute !important;
              top: 15px !important;
            }
          }
          @media only screen and (max-device-width: 767px) {
            .ReactModal__Content {
              min-width: 95% !important;
            }
          }
          /* TO-DO: DOES NOT APPLY TO CAPTURE MODAL OR VIDEO PLAYER MODAL */
          @media only screen and (max-height: 560px) {
            .ReactModal__Content {
              position: absolute !important;
              top: 15px !important;
            }
          }
        `}
      />
      <ReactModal
        closeTimeoutMS={250}
        isOpen={!!isOpen}
        onRequestClose={modalClose}
        style={ModalStyle(overlayStyles, contentStyles)}
        className='global-modal'
        overlayClassName='overlay'
      >
        <ModalContentWrapper ref={modalContentRef} modal={modalName}>
          {children}
        </ModalContentWrapper>
      </ReactModal>
    </>
  );
};

if (process.env.STORYBOOK_RUNNING === undefined) {
  ReactModal.setAppElement('#app');
}

FeedModalWrapper.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  onRequestClose: PropTypes.func.isRequired,
  overlayStyles: PropTypes.instanceOf(Object),
  contentStyles: PropTypes.instanceOf(Object),
  modalName: PropTypes.string,
};

FeedModalWrapper.defaultProps = {
  children: '',
  overlayStyles: {},
  contentStyles: {},
  modalName: '',
};

export default FeedModalWrapper;
